import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageLayout from '@/components/feature/PageLayout';
import FAQ from '@/pages/home/components/FAQ';
import LinkedInFeed from '@/pages/home/components/LinkedInFeed';

const faqSchemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How quickly can you provide engineers after we submit a request?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most clients receive their first shortlisted candidate profiles within 48 hours of submitting a hiring request. For urgent needs, we can accelerate this to 24 hours. The typical time from request to engineer onboarding is 5 to 10 business days, depending on the complexity of the role and any client-specific onboarding requirements."
      }
    },
    {
      "@type": "Question",
      "name": "What is your vetting process for developers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Every engineer goes through a rigorous 4-stage vetting process: (1) Resume and portfolio screening against verified past projects, (2) Live technical interview with a senior engineer covering system design and coding, (3) Domain-specific assessment tailored to the client's tech stack, and (4) Soft skills and communication evaluation. Only the top 8% of applicants make it through."
      }
    },
    {
      "@type": "Question",
      "name": "Can we interview candidates before making a decision?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. We present you with 2 to 4 pre-vetted candidate profiles per role, and you have full control over whether to conduct your own interviews, pair-programming sessions, or take-home assignments. There is no obligation to hire until you are fully satisfied with the match."
      }
    },
    {
      "@type": "Question",
      "name": "Are your engineers full-time or part-time?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer flexible engagement models. Most clients hire engineers on a full-time dedicated basis (40 hours per week). However, we also support part-time arrangements (20+ hours), team augmentation for specific sprints, and project-based contracts. You can scale up or down with 2 weeks notice."
      }
    },
    {
      "@type": "Question",
      "name": "What happens if an engineer is not a good fit?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer a risk-free trial period and a satisfaction guarantee. If an engineer does not meet expectations during the first 2 weeks, we replace them at no cost. Even beyond that period, we provide a seamless replacement process with a 1-week knowledge transfer window to ensure continuity."
      }
    },
    {
      "@type": "Question",
      "name": "Do your engineers work in our time zone?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We match engineers based on your preferred working hours and time zone overlap requirements. For US clients, we typically provide 4 to 8 hours of overlap. For European clients, we offer near-full overlap. We also have onshore and nearshore options for clients who need 100% timezone alignment."
      }
    },
    {
      "@type": "Question",
      "name": "What engagement models do you offer?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer four primary models: (1) Staff Augmentation — individual engineers who integrate into your existing team, (2) Dedicated Development Team — a fully managed autonomous squad with a Tech Lead, (3) Contract Hiring — short-term or long-term contractors for specific projects, and (4) Offshore Development Center — a scalable remote delivery hub with ongoing support."
      }
    },
    {
      "@type": "Question",
      "name": "How do you handle intellectual property and data security?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All engagements are covered by comprehensive NDAs, IP assignment agreements, and data protection clauses. Our engineers work on client-managed infrastructure or secure isolated environments. We are SOC 2 Type II compliant and can accommodate HIPAA, GDPR, and PCI-DSS requirements."
      }
    },
    {
      "@type": "Question",
      "name": "Can we manage the engineers directly, or do you handle everything?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You have full flexibility. With staff augmentation, engineers report directly to your engineering managers and attend your standups. With dedicated teams, we provide a Tech Lead who handles day-to-day management while you set priorities and direction. You choose the level of oversight that works for you."
      }
    },
    {
      "@type": "Question",
      "name": "How is pricing structured?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Pricing is transparent and based on the engineer's seniority, technology specialty, and engagement model. Junior developers typically range from $35 to $55 per hour, mid-level from $55 to $85 per hour, and senior or specialized engineers from $85 to $140 per hour. We also offer fixed monthly retainers for dedicated teams."
      }
    },
    {
      "@type": "Question",
      "name": "Are there any setup fees or hidden costs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No setup fees, no hidden costs. You only pay for the hours worked or the agreed monthly retainer. We provide detailed timesheets and activity reports weekly. For dedicated teams, there is a flat monthly management fee that covers the Tech Lead, HR support, and infrastructure."
      }
    },
    {
      "@type": "Question",
      "name": "What is your billing cycle?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We bill monthly in arrears with net-15 payment terms. For long-term engagements, we can accommodate net-30 terms upon request. Invoices include detailed breakdowns by engineer, hours worked, and any approved expenses. We accept wire transfers, ACH, and major credit cards."
      }
    }
  ]
};

function PageHero({ title, subtitle }: { title: string; subtitle: string }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-12 md:pt-32 md:pb-16 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
            <i className="ri-question-answer-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Common Questions</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4">
            {title}
          </h1>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  useEffect(() => {
    const scriptId = 'faq-schema-ld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqSchemaData);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <PageLayout title="FAQ — Remote Developer Hiring Questions Answered" description="Answers to everything about hiring remote developers with Thoughtic — pricing, process, vetting, time zones, engagement models, and more." keywords="remote developer hiring FAQ, staff augmentation questions, IT staffing FAQ, how to hire engineers, remote developer vetting process, engagement model questions, offshore development FAQ">
      <PageHero
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about working with Thoughtic Solutions."
      />
      <FAQ />
      <LinkedInFeed />
    </PageLayout>
  );
}